import React, { cloneElement, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { LinearProgress } from 'react-admin';
import Typography from '@mui/material/Typography';

interface Props {
    open: boolean;
    onClose?: any;
    children: any;
    extraInfo?: any;
    linear?: boolean;
    step?: number;
    totalStep?: number;
    withTransition?: boolean;
    backdropalpha?: 'normal' | 'soft';
    maxWidth?: 'sm' | 'md' | 'lg' | 'xs';
    title?: string;
    icon?: any;
    titleUppercase?: boolean;
    closeButtonDisabled?: boolean;
    overflow?: 'auto' | 'hidden';
    titleActions?: React.ReactNode;
    /**
     * Si es true, el cuerpo del diálogo es el único scroll vertical (grid minmax + altura máxima).
     * Por defecto false: mismo layout que antes del ajuste de agenda.
     */
    unifiedBodyScroll?: boolean;
}

const DialogTemplate = ({
    onClose,
    open,
    children,
    extraInfo,
    withTransition = false,
    linear = false,
    step = 1,
    totalStep = 3,
    maxWidth = 'sm',
    icon,
    title,
    titleUppercase = true,
    closeButtonDisabled = false,
    overflow = 'auto',
    titleActions,
    unifiedBodyScroll = false,
    ...rest
}: Props) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const [sentinelRef, setSentinelRef] = useState<any>(null);
    const [, setIsSticky] = useState(false);

    const stopEventPropagation = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    const navigateBack = () => {
        navigate(-1);
    };

    const handleOnClose = (_e: any, reason: 'escapeKeyDown' | 'backdropClick') => {
        if (reason === 'backdropClick') return;

        if (onClose) {
            onClose();
        } else {
            navigateBack();
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsSticky(!entry.isIntersecting);
            },
            { threshold: [1] },
        );

        if (sentinelRef !== null) {
            observer.observe(sentinelRef.current);
        }

        return () => {
            if (sentinelRef !== null) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [sentinelRef]);

    return (
        <Dialog
            onClick={stopEventPropagation}
            open={open}
            onClose={handleOnClose}
            {...(isLargeScreen ? {} : { fullScreen: true })}
            maxWidth={maxWidth}
            fullWidth
            transitionDuration={0}
            sx={{
                '& .MuiPaper-root': {
                    display: 'grid',
                    gridTemplateRows: unifiedBodyScroll
                        ? '0px auto minmax(0, 1fr)'
                        : '0px auto 1fr',
                    ...(unifiedBodyScroll && {
                        // Sin alignContent: 'start', stretch reparte espacio en filas auto y el sentinel
                        // vacío puede dejar un hueco grande entre cabecera y cuerpo.
                        alignContent: 'start',
                        overflow: 'hidden',
                        gap: 0,
                        p: 0,
                    }),
                    ...(unifiedBodyScroll && isLargeScreen && { maxHeight: 'min(90vh, 100%)' }),
                    ...(isLargeScreen && { borderRadius: 2 }),
                },
                zIndex: 50,
                overflow,
            }}
            {...rest}
        >
            <Box
                ref={(element: any) => {
                    if (element && !sentinelRef) {
                        setSentinelRef({ current: element });
                    }
                }}
                sx={{
                    height: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    margin: 0,
                    padding: 0,
                    lineHeight: 0,
                }}
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    zIndex: 3,
                    position: 'sticky',
                    top: 0,
                    transition: 'box-shadow 0.2s',
                    minHeight: '55px',
                    px: 2,
                    gap: 1,
                    ...(unifiedBodyScroll && {
                        bgcolor: 'background.paper',
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }),
                }}
            >
                {linear && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            zIndex: 10,
                            width: '100%',
                            height: '5px',
                        }}
                    >
                        <LinearProgress
                            sx={{
                                m: 0,
                                width: '100%',
                                height: '100%',
                            }}
                            variant='determinate'
                            data-cy={`linearProgress-${(100 / totalStep) * step}`}
                            value={(100 / totalStep) * step}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        color: (theme) => theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    {title && icon && cloneElement(icon)}

                    {title ? (
                        <Typography
                            component='div'
                            sx={{
                                ...(titleUppercase && { textTransform: 'uppercase' }),
                                fontSize: 14,
                                fontWeight: 700,
                                lineHeight: 1,
                                m: 0,
                                color: (theme) => theme.palette.primary.main,
                            }}
                        >
                            {title}
                        </Typography>
                    ) : (
                        <span />
                    )}

                    {titleActions && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {titleActions}
                        </Box>
                    )}
                </Box>

                <Box>
                    <IconButton
                        onClick={onClose ? onClose : navigateBack}
                        data-cy='close-button'
                        disabled={closeButtonDisabled}
                        color='primary'
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    alignContent: 'start',
                    px: 2,
                    // Mismo token que el padding horizontal (16px): ritmo uniforme respecto a la cabecera (px: 2).
                    pt: unifiedBodyScroll ? 2 : undefined,
                    pb: 2,
                    ...(unifiedBodyScroll
                        ? {
                              overflowY: overflow === 'hidden' ? 'hidden' : 'auto',
                              overflowX: 'hidden',
                              minHeight: 0,
                          }
                        : {
                              overflow,
                          }),
                }}
            >
                {children}
            </Box>
        </Dialog>
    );
};

export default DialogTemplate;
